import { ReactNode, useState } from "react";
import {
  Button,
  ButtonVariant,
  Spinner,
  Text,
  TextField,
  Toggle,
} from "@merge-api/merge-javascript-shared";
import {
  Check,
  ChevronRight,
  ListChecks,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Trash2,
  X,
} from "lucide-react";
import MergeTable from "../../shared/MergeTable";
import IntegrationBuilderEmptyState from "../../integration-builder/shared/IntegrationBuilderEmptyState";
import { Playbook, PlaybookEntry, PlanSummary } from "../types";
import { displaySourceType, formatCadence, specificityLabel } from "../helpers";
import { showErrorToast, showSuccessToast } from "../../shared/Toasts";
import { conductorDelete, conductorPost } from "../conductorApi";
import { useConductorResource } from "../hooks/useConductorResource";
import ConductorCard from "./ConductorCard";
import { KV, Mono, Muted } from "./ConductorFields";
import { EnabledBadge } from "./ConductorBadges";
import { Select } from "./ConductorFormFields";
import InlinePlanCreate from "./InlinePlanCreate";
import { Dialog } from "@merge-api/merge-javascript-shared/dist/designSystem/molecules/Dialog";

interface Props {
  playbookId: string;
  onOpenPlan: (planId: string) => void;
  onDeleted: () => void;
}

// The working copy of the entry being added/edited inline. id === null is a new
// (admin-authored) entry; a string id edits that existing entry.
interface EntryDraft {
  id: string | null;
  planId: string;
  cadence: string;
  enabled: boolean;
}

const ConductorPlaybookDetail = ({ playbookId, onOpenPlan, onDeleted }: Props) => {
  const [reloadToken, setReloadToken] = useState(0);
  const [planSummariesReloadToken, setPlanSummariesReloadToken] = useState(0);
  const [editing, setEditing] = useState<EntryDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletingPlaybook, setDeletingPlaybook] = useState(false);
  // The status the confirm dialog is asking about; null = dialog closed.
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);
  const [savingEnabled, setSavingEnabled] = useState(false);
  const { data: playbook, isLoading } = useConductorResource<Playbook>(
    `/conductor/playbooks/${playbookId}`,
    "Error fetching playbook",
    reloadToken
  );
  const { data: planSummaries } = useConductorResource<PlanSummary[]>(
    playbook?.integration_id
      ? `/conductor/plan-summaries?integration_id=${playbook.integration_id}`
      : null,
    undefined,
    planSummariesReloadToken
  );
  const planOptions = (planSummaries || [])
    .filter((plan) => !playbook?.category || plan.category === playbook.category)
    .map((plan) => ({ id: plan.id, name: plan.name }));

  const patch = (changes: Partial<EntryDraft>) =>
    setEditing((draft) => (draft ? { ...draft, ...changes } : draft));

  const cadenceNumber = editing ? Number(editing.cadence) : 0;
  const canSave =
    !!editing &&
    editing.planId !== "" &&
    Number.isFinite(cadenceNumber) &&
    cadenceNumber > 0 &&
    !busy;

  const save = async () => {
    if (!editing || !playbook) return;
    setBusy(true);
    const original = editing.id ? playbook.entries.find((e) => e.id === editing.id) : null;
    const body: Record<string, unknown> = {
      ...(editing.id ? { id: editing.id } : {}),
      playbook_id: playbookId,
      plan_id: editing.planId,
      cadence_minutes: cadenceNumber,
      enabled: editing.enabled,
    };
    // Preserve an existing entry's source identity (paired, not editable here).
    if (original?.source_type && original?.source_id) {
      body.source_type = original.source_type;
      body.source_id = original.source_id;
    }
    const { ok, error } = await conductorPost("/conductor/playbook-entries", body);
    setBusy(false);
    if (ok) {
      showSuccessToast(editing.id ? "Entry updated" : "Plan attached");
      setEditing(null);
      setReloadToken((token) => token + 1);
    } else {
      showErrorToast(error || "Save failed");
    }
  };

  const remove = async () => {
    if (!editing?.id) return;
    setBusy(true);
    const { ok, error } = await conductorDelete(`/conductor/playbook-entries/${editing.id}`);
    setBusy(false);
    if (ok) {
      showSuccessToast("Entry removed");
      setEditing(null);
      setReloadToken((token) => token + 1);
    } else {
      showErrorToast(error || "Remove failed");
    }
  };

  // Scope is immutable (it decides which linked accounts resolve to this
  // playbook), so only `enabled` is editable. The upsert replaces the entry
  // list with whatever the body carries, so the existing entries must be sent
  // back or they are deleted. Entries reference their plan by id only, which
  // links to the plan without rewriting its steps.
  const setEnabled = async (next: boolean) => {
    if (!playbook) return;
    setSavingEnabled(true);
    const { ok, error } = await conductorPost("/conductor/playbooks", {
      id: playbook.id,
      integration_id: playbook.integration_id,
      organization_id: playbook.organization_id,
      linked_account_id: playbook.linked_account_id,
      category: playbook.category,
      enabled: next,
      entries: playbook.entries.map((entry) => ({
        id: entry.id,
        playbook_id: playbook.id,
        plan_id: entry.plan_id,
        cadence_minutes: entry.cadence_minutes,
        enabled: entry.enabled,
        ...(entry.source_type && entry.source_id
          ? { source_type: entry.source_type, source_id: entry.source_id }
          : {}),
      })),
    });
    setSavingEnabled(false);
    setPendingEnabled(null);
    if (ok) {
      showSuccessToast(next ? "Playbook enabled" : "Playbook disabled");
      setReloadToken((token) => token + 1);
    } else {
      showErrorToast(error || "Failed to update playbook");
    }
  };

  const deletePlaybook = async () => {
    setDeletingPlaybook(true);
    const { ok, error } = await conductorDelete(`/conductor/playbooks/${playbookId}`);
    setDeletingPlaybook(false);
    if (ok) {
      showSuccessToast("Playbook deleted");
      onDeleted();
    } else {
      showErrorToast(error || "Failed to delete playbook");
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (!playbook) {
    return (
      <Text variant="md" className="block py-16 text-center text-gray-60">
        Playbook not found.
      </Text>
    );
  }

  const displayRow = (entry: PlaybookEntry): ReactNode => (
    <tr key={entry.id}>
      <td>
        <Text variant="md" className="font-medium">
          {entry.plan?.name ? (
            <>
              {entry.plan.name}{" "}
              <span className="font-mono font-normal text-gray-60">({entry.plan_id})</span>
            </>
          ) : (
            <span className="font-mono">{entry.plan_id}</span>
          )}
        </Text>
      </td>
      <td>
        {entry.source_type ? (
          <Text variant="md" className="text-gray-80">
            {displaySourceType(entry.source_type)}{" "}
            <span className="font-mono font-normal text-gray-60">({entry.source_id})</span>
          </Text>
        ) : (
          <Muted>None</Muted>
        )}
      </td>
      <td>
        <Mono>{formatCadence(entry.cadence_minutes)}</Mono>
      </td>
      <td>
        <EnabledBadge enabled={entry.enabled} />
      </td>
      <td>
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() =>
              setEditing({
                id: entry.id,
                planId: entry.plan_id ?? "",
                cadence: String(entry.cadence_minutes),
                enabled: entry.enabled,
              })
            }
            aria-label="Edit entry"
            className="rounded p-1 text-gray-60 hover:bg-gray-10 hover:text-gray-90"
          >
            <Pencil size={18} />
          </button>
          {entry.plan_id && (
            <button
              onClick={() => entry.plan_id && onOpenPlan(entry.plan_id)}
              aria-label="Open plan"
              className="rounded p-1 text-gray-60 hover:bg-gray-10 hover:text-gray-90"
            >
              <ChevronRight size={22} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );

  const editRow = (isNew: boolean): ReactNode => {
    if (!editing) return null;
    const original = editing.id ? playbook.entries.find((e) => e.id === editing.id) : null;
    const planListed = planOptions.some((option) => option.id === editing.planId);
    return (
      <tr key={isNew ? "new-entry" : editing.id ?? "edit"} className="bg-[#F9FAFB]">
        <td>
          <div className="flex items-center gap-2">
            <div className="min-w-0 max-w-md flex-1">
              <Select value={editing.planId} onChange={(value) => patch({ planId: value })}>
                <option value="">Select a plan…</option>
                {!planListed && editing.planId && (
                  <option value={editing.planId}>{editing.planId}</option>
                )}
                {planOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name} ({option.id})
                  </option>
                ))}
              </Select>
            </div>
            <InlinePlanCreate
              integrationId={playbook.integration_id}
              category={playbook.category}
              onCreated={(planId) => {
                patch({ planId });
                setPlanSummariesReloadToken((token) => token + 1);
              }}
            />
          </div>
        </td>
        <td>
          {original?.source_type ? (
            <Text variant="md" className="text-gray-80">
              {displaySourceType(original.source_type)}{" "}
              <span className="font-mono font-normal text-gray-60">({original.source_id})</span>
            </Text>
          ) : (
            <Muted>None</Muted>
          )}
        </td>
        <td>
          <div className="flex items-center gap-2">
            <div className="w-16">
              <TextField
                className="bg-white"
                type="number"
                value={editing.cadence}
                onChange={(e) => patch({ cadence: e.target.value })}
              />
            </div>
            <Text variant="sm" className="text-gray-60">
              min
            </Text>
          </div>
        </td>
        <td>
          <Toggle checked={editing.enabled} onChange={(value) => patch({ enabled: value })} />
        </td>
        <td>
          <div className="flex items-center justify-end gap-2">
            {!isNew && (
              <button
                onClick={remove}
                disabled={busy}
                aria-label="Remove entry"
                className="rounded p-1 text-red-60 hover:bg-gray-10 disabled:opacity-40"
              >
                <Trash2 size={18} />
              </button>
            )}
            <button
              onClick={() => setEditing(null)}
              disabled={busy}
              aria-label="Cancel edit"
              className="rounded p-1 text-gray-60 hover:bg-gray-10 hover:text-gray-90 disabled:opacity-40"
            >
              <X size={18} />
            </button>
            <button
              onClick={save}
              disabled={!canSave}
              aria-label="Save entry"
              className="rounded p-1 text-green-60 hover:bg-gray-10 disabled:opacity-40"
            >
              <Check size={18} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const rows: ReactNode[] = playbook.entries.map((entry) =>
    editing && editing.id === entry.id ? editRow(false) : displayRow(entry)
  );
  if (editing && editing.id === null) rows.push(editRow(true));
  const showEmptyState = playbook.entries.length === 0 && !(editing && editing.id === null);

  return (
    <div className="pb-8">
      {/* Page header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <Text variant="h4">Playbook</Text>
            <EnabledBadge enabled={playbook.enabled} />
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={ButtonVariant.SecondaryGray}
              leftIcon={playbook.enabled ? <PowerOff size={14} /> : <Power size={14} />}
              loading={savingEnabled}
              onClick={() => setPendingEnabled(!playbook.enabled)}
            >
              {playbook.enabled ? "Disable playbook" : "Enable playbook"}
            </Button>
            <Button
              size="sm"
              variant={ButtonVariant.DangerText}
              leftIcon={<Trash2 size={14} />}
              onClick={() => setConfirmDelete(true)}
            >
              Delete playbook
            </Button>
          </div>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <Text variant="sm" className="text-gray-60">
            {specificityLabel(playbook.organization_id, playbook.linked_account_id)}
          </Text>
          <span className="text-gray-40">·</span>
          <Text variant="sm" className="font-mono text-gray-60">
            {playbook.id}
          </Text>
        </div>
      </div>

      <ConductorCard
        title="Resolution"
        subtitle="How this playbook is selected for incoming linked accounts"
        padBody
      >
        <dl className="grid grid-cols-[180px_1fr] items-center gap-y-3">
          <KV label="Integration">
            <Mono>{playbook.integration_id}</Mono>
          </KV>
          <KV label="Category">
            {playbook.category ? <Mono>{playbook.category}</Mono> : <Muted>none</Muted>}
          </KV>
          <KV label="Organization">
            {playbook.organization_id ? (
              <Mono>{playbook.organization_id}</Mono>
            ) : (
              <Muted>null (matches all orgs)</Muted>
            )}
          </KV>
          <KV label="Linked Account">
            {playbook.linked_account_id ? (
              <Mono>{playbook.linked_account_id}</Mono>
            ) : (
              <Muted>null (matches all linked accounts in scope)</Muted>
            )}
          </KV>
        </dl>
      </ConductorCard>

      <ConductorCard
        title="Entries"
        subtitle={`${playbook.entries.length}`}
        actions={
          <Button
            size="sm"
            variant={ButtonVariant.SecondaryGray}
            leftIcon={<Plus size={14} />}
            disabled={!!editing}
            onClick={() => setEditing({ id: null, planId: "", cadence: "60", enabled: true })}
          >
            Add entry
          </Button>
        }
      >
        <MergeTable
          hover={false}
          isOverviewPage
          // Fixed layout and a uniform row height, so swapping a row into its
          // inline editor doesn't resize every other column.
          className="[&_table]:table-fixed [&_td]:h-20"
          header={
            <>
              <th scope="col">Plan</th>
              <th scope="col">Source</th>
              <th scope="col" className="w-40">
                Cadence
              </th>
              <th scope="col" className="w-32">
                Status
              </th>
              <th scope="col" className="w-36" />
            </>
          }
          content={
            showEmptyState ? (
              <IntegrationBuilderEmptyState
                title="No entries"
                subtitle="This playbook has no entries yet."
                numberOfColumns={5}
                withIcon
                icon={ListChecks}
              />
            ) : (
              <>{rows}</>
            )
          }
        />
      </ConductorCard>

      <Dialog
        open={pendingEnabled !== null}
        onClose={() => setPendingEnabled(null)}
        variant="sm"
        title={pendingEnabled ? "Enable playbook?" : "Disable playbook?"}
        primaryButtonText={pendingEnabled ? "Enable playbook" : "Disable playbook"}
        primaryButtonVariant={
          pendingEnabled ? ButtonVariant.PrimaryGray : ButtonVariant.DangerFilled
        }
        primaryButtonLoading={savingEnabled}
        onPrimaryButtonClick={() => setEnabled(pendingEnabled ?? false)}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={() => setPendingEnabled(null)}
      >
        <Text variant="md" className="text-gray-70">
          Are you sure you want to {pendingEnabled ? "enable" : "disable"} this playbook?
        </Text>
      </Dialog>

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        variant="sm"
        title="Delete playbook?"
        primaryButtonText="Delete playbook"
        primaryButtonVariant={ButtonVariant.DangerFilled}
        primaryButtonLoading={deletingPlaybook}
        onPrimaryButtonClick={deletePlaybook}
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={() => setConfirmDelete(false)}
      >
        <Text variant="md" className="text-gray-70">
          This permanently deletes the playbook and all {playbook.entries.length} of its entries.
          The plans themselves are not deleted.
        </Text>
      </Dialog>
    </div>
  );
};

export default ConductorPlaybookDetail;
